import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, Phone, MapPin, Send, ChevronDown, CalendarDays, Globe, Users, Home } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import api from '../lib/api';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    agreeToTerms: false
  });

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Refs for animations
  const contactSectionRef = useRef(null);
  const contactInfoRef = useRef(null);
  const faqSectionRef = useRef(null);
  const formRef = useRef(null);
  const additionalInfoRef = useRef(null);

  // FAQ data with more detailed information
  const faqs = [
    {
      question: "How does AI trip planning work?",
      answer: "Our AI analyzes your preferences, budget, and schedule to create personalized itineraries and match you with compatible travel buddies. It considers factors like travel history, interests, and seasonal trends to suggest the best destinations and activities for you."
    },
    {
      question: "Is the service free?",
      answer: "We offer a free tier with basic features including trip planning and buddy matching. Premium plans unlock advanced AI recommendations, priority matching, exclusive destinations, and 24/7 travel concierge support."
    },
    {
      question: "How do I join the waitlist?",
      answer: "Simply fill out the form on our homepage or contact us directly. We'll notify you when we launch in your area. Early waitlist members receive exclusive perks and discounts on premium plans."
    },
    {
      question: "How does travel buddy matching work?",
      answer: "Our sophisticated matching algorithm considers personality traits, travel preferences, budget ranges, and availability to pair you with compatible travel companions. You can also set specific criteria for your ideal travel buddy."
    },
    {
      question: "Can I customize my itinerary?",
      answer: "Absolutely! While our AI generates initial recommendations, you have full control to modify, add, or remove activities. Our platform makes it easy to adjust your plans based on real-time conditions or personal preferences."
    }
  ];

  // Toggle FAQ accordion
  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    if (!formData.agreeToTerms) errors.agreeToTerms = 'You must agree to terms';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        await api.post('/api/contact/submit', formData);
        
        // Show success message
        gsap.to('.submit-success', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          onComplete: () => {
            setTimeout(() => {
              gsap.to('.submit-success', {
                opacity: 0,
                y: 20,
                duration: 0.5
              });
            }, 3000);
          }
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          agreeToTerms: false
        });
      } catch (error) {
        gsap.to('.submit-error', {
          opacity: 1,
          y: 0,
          duration: 0.5,
          onComplete: () => {
            setTimeout(() => {
              gsap.to('.submit-error', {
                opacity: 0,
                y: 20,
                duration: 0.5
              });
            }, 3000);
          }
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Animation effects
  useEffect(() => {
    // Animate contact info section
    gsap.fromTo(contactInfoRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: contactInfoRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animate FAQ section
    gsap.fromTo(faqSectionRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: faqSectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animate form section
    gsap.fromTo(formRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: formRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animate additional info section
    gsap.fromTo(additionalInfoRef.current,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: additionalInfoRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Clean up ScrollTrigger instances
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-12 px-4" ref={contactSectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get in Touch</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Have questions about Sync and Explore? We're here to help you plan your perfect trip. 
            Reach out to us and start your journey today.
          </p>
        </div>

        <div className="grid lg:grid-col gap-12" ref={contactInfoRef}>

             <Card className="shadow-lg hover:shadow-xl transition-all duration-300" ref={faqSectionRef}>
              <CardHeader>
                <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 hover:border-teal-300"
                  >
                    <button
                      className="w-full flex justify-between items-center p-4 text-left bg-white hover:bg-teal-50 transition-colors duration-300"
                      onClick={() => toggleFaq(index)}
                    >
                      <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                      <ChevronDown 
                        className={`w-5 h-5 text-teal-600 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96' : 'max-h-0'}`}
                    >
                      <div className="p-4 bg-gray-50">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

          {/* Contact Information */}
          <div className=" gap-12 grid grid-cols-1 lg:grid-cols-2" ref={formRef} >
            <Card className="shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <div className="p-3 bg-teal-100 rounded-lg">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Email us</h3>
                    <p className="text-gray-700 mt-1">contact@syncandexplore.com</p>
                    <p className="text-sm text-gray-500 mt-2">For general inquiries, support, business partnerships, and collaborations.</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <div className="p-3 bg-teal-100 rounded-lg">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Call Us</h3>
                    <p className="text-gray-700 mt-1">+91 6391433858</p>
                    <p className="text-sm text-gray-500 mt-2">Monday to Friday, 9:00 AM - 6:00 PM IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <div className="p-3 bg-teal-100 rounded-lg">
                    <MapPin className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">Customer Support</h3>
                    <p className="text-gray-700 mt-1">Our support team is available around the clock to help you with any queries.</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-600 text-xs rounded-full">Trip Planning</span>
                      <span className="px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-600 text-xs rounded-full">Booking Issues</span>
                      <span className="px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-600 text-xs rounded-full">Account Help</span>
                      <span className="px-2 py-1 sm:px-3 sm:py-1 bg-teal-100 text-teal-600 text-xs rounded-full">Technical Support</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

          {/* Message content */}
             <Card className="shadow-lg hover:shadow-xl transition-all duration-300" >
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Send className="w-6 h-6 text-teal-600" />
                  </div>
                  Send us a Message
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full ${formErrors.name ? 'border-red-500' : ''}`}
                        placeholder="Your full name"
                      />
                      {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full ${formErrors.email ? 'border-red-500' : ''}`}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full ${formErrors.phone ? 'border-red-500' : ''}`}
                        placeholder="+91 1234567890"
                      />
                      {formErrors.phone && <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>}
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={`w-full ${formErrors.subject ? 'border-red-500' : ''}`}
                        placeholder="What's this about?"
                      />
                      {formErrors.subject && <p className="text-red-500 text-sm mt-1">{formErrors.subject}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className={`w-full min-h-[150px] ${formErrors.message ? 'border-red-500' : ''}`}
                      placeholder="Tell us how we can help you..."
                    />
                    {formErrors.message && <p className="text-red-500 text-sm mt-1">{formErrors.message}</p>}
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      id="agreeToTerms"
                      name="agreeToTerms"
                      type="checkbox"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                      I agree to the terms and conditions and privacy policy *
                    </label>
                  </div>
                  {formErrors.agreeToTerms && <p className="text-red-500 text-sm">{formErrors.agreeToTerms}</p>}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-teal-600 hover:bg-teal-700 hover:shadow-lg text-white py-3 px-6 rounded-lg font-medium transition-all duration-300 flex items-center justify-center disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {/* Success message */}
                  <div className="submit-success opacity-0 transform translate-y-5 transition-all duration-500 text-center py-3 px-4 bg-green-100 text-green-700 rounded-lg">
                    Thank you for your message! We will get back to you soon.
                  </div>
                  
                  {/* Error message */}
                  <div className="submit-error opacity-0 transform translate-y-5 transition-all duration-500 text-center py-3 px-4 bg-red-100 text-red-700 rounded-lg">
                    Failed to send message. Please try again.
                  </div>
                </form>
              </CardContent>
            </Card>
           
            
          </div>

      {/* Features */}
      
          {/* <Card className="shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Globe className="w-6 h-6 text-teal-600" />
                  </div>
                  Why Choose Us?
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <CalendarDays className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium">Smart Scheduling</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <Users className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium">Buddy Matching</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <Home className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium">Best Stays</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-teal-50 transition-colors duration-300">
                  <Globe className="w-5 h-5 text-teal-600" />
                  <span className="text-sm font-medium">Global Destinations</span>
                </div>
              </CardContent>
            </Card> */}
       

        {/* Additional Info */}
        <div className=" text-center" ref={additionalInfoRef}>
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 max-w-4xl mx-auto hover:shadow-xl transition-all duration-300">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Start Your Adventure?</h2>
            <p className="text-gray-600 mb-8 text-lg">
              Join thousands of travelers who are discovering new destinations with AI-powered planning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-teal-600 hover:bg-teal-700 hover:shadow-lg text-white px-8 py-4 text-lg rounded-lg transition-all duration-300">
                Join Waitlist
              </Button>
              <Button variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50 hover:shadow-lg px-8 py-4 text-lg rounded-lg transition-all duration-300">
                Learn More
              </Button>
            </div>
            
            {/* Stats section */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">10K+</div>
                <div className="text-gray-600">Happy Travelers</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">50+</div>
                <div className="text-gray-600">Destinations</div>
              </div>
              <div className="p-4">
                <div className="text-3xl font-bold text-teal-600 mb-2">98%</div>
                <div className="text-gray-600">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default Contact;

///
